import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    AuthService,
    getEmptyAccountTemplateLibraryPath,
    userHasTasks,
} from 'utility'

/**
 * Redirect brand-new accounts (no templates + no tasks) to the template library.
 * Uses a lightweight has-tasks check (PageSize=1) — never the full InspectionTask dump.
 */
export function useEmptyAccountTemplateRedirect({ loading, data }) {
    const navigate = useNavigate()
    const location = useLocation()
    const hadLoading = useRef(false)
    const [taskCheckDone, setTaskCheckDone] = useState(false)
    const [hasTasks, setHasTasks] = useState(true)

    useEffect(() => {
        if (loading) {
            hadLoading.current = true
        }
    }, [loading])

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const exists = await userHasTasks()
                if (!cancelled) {
                    setHasTasks(!!exists)
                    setTaskCheckDone(true)
                }
            } catch {
                if (!cancelled) {
                    // Fail open: assume tasks exist so we don't force-redirect.
                    setHasTasks(true)
                    setTaskCheckDone(true)
                }
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        try {
            if (location.pathname === '/app/templates/new-template-library') {
                return
            }

            const templatesLoaded = hadLoading.current && !loading
            if (!templatesLoaded || !taskCheckDone) {
                return
            }

            const templatesArray = Array.isArray(data) ? data : []
            const templatesEmpty = templatesArray.length === 0

            if (!templatesEmpty || hasTasks) {
                return
            }

            const libraryPath = getEmptyAccountTemplateLibraryPath(
                AuthService.getUser(),
            )
            if (libraryPath) {
                navigate(libraryPath, { replace: true })
            }
        } catch (err) {
            // ignore storage errors
        }
    }, [
        loading,
        data,
        taskCheckDone,
        hasTasks,
        navigate,
        location.pathname,
    ])
}
